import _ from 'lodash';
import { useState, useEffect } from 'react';
import API from '@scale/frontend-lib/dashboard-api';
import { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';

export function useProjectAttachmentHipaaCoverage(projectId?: string) {
  const [isLoading, setIsLoading] = useState(true);
  const [isProjectAttachmentCoveredByHipaa, setIsProjectAttachmentCoveredByHipaa] = useState(false);

  useEffect(() => {
    (async function () {
      const { isAttachmentCoveredByHipaa } =
        await API.selfserve.fetchProjectAttachmentHipaaCoverage({ project: projectId });
      setIsProjectAttachmentCoveredByHipaa(isAttachmentCoveredByHipaa);
      setIsLoading(false);
    })();
  }, [projectId]);

  const refresh = async () => {
    const { isAttachmentCoveredByHipaa } = await API.selfserve.fetchProjectAttachmentHipaaCoverage({
      project: projectId,
    });
    setIsProjectAttachmentCoveredByHipaa(isAttachmentCoveredByHipaa);
  };

  return { isLoading, isProjectAttachmentCoveredByHipaa, refresh };
}

export enum TourStep {
  Clear = '',
  CreateANewProject = 'create_a_new_project',
  SelectTemplate = 'select_template',
  SampleDataset = 'sample_dataset',
  AddData = 'add_data',
  DataImported = 'data_imported',
  UseCase = 'use_case',
  UseCaseTemplate = 'use_case_template',
  Pipeline = 'pipeline',
  UseCaseNext = 'use_case_next',
  Label = 'label',
  TemplateLabel = 'template_label',
  PreviewTask = 'preview_task',
  TaxonomyNext = 'taxonomy_next',
  Instructions = 'instructions',
  PreviewInstructions = 'preview_instructions',
  InstructionsNext = 'instructions_next',
  BatchNext = 'batch_next',
  LaunchBatch = 'launch_batch',
}

export interface TourStepElem {
  name: TourStep;
  text: string;
  placement: $FIXME;
  hasNext?: boolean;
}

export function useOnboardingV3Tour(firstTimeTemplate: boolean, showTour: boolean) {
  const [shouldEnterTour, setShouldEnterTour] = useState<boolean | null>(null);
  const [tourIndex, setTourIndex] = useState(0);
  const [closedTourSteps, setClosedTourSteps] = useState<TourStep[]>([]);

  const [tourRef, setTourRef] = useState(null);

  const tourOrder = firstTimeTemplate
    ? [
        TourStep.CreateANewProject,
        TourStep.SelectTemplate,
        TourStep.AddData,
        TourStep.UseCaseTemplate,
        TourStep.TemplateLabel,
        TourStep.PreviewTask,
        TourStep.TaxonomyNext,
        TourStep.Instructions,
        TourStep.PreviewInstructions,
        TourStep.InstructionsNext,
        TourStep.BatchNext,
        TourStep.LaunchBatch,
      ]
    : [
        TourStep.CreateANewProject,
        TourStep.SelectTemplate, // need this here since firstTimeTemplate isn't set to true yet
        TourStep.SampleDataset,
        TourStep.DataImported,
        TourStep.UseCase,
        TourStep.Pipeline,
        TourStep.UseCaseNext,
        TourStep.Label,
        TourStep.PreviewTask,
        TourStep.TaxonomyNext,
        TourStep.Instructions,
        TourStep.PreviewInstructions,
        TourStep.InstructionsNext,
        TourStep.BatchNext,
        TourStep.LaunchBatch,
      ];

  const handleNextTourStep = () => {
    if (tourIndex + 1 >= tourOrder.length) {
      setTourIndex(-1);
    } else {
      setTourIndex(tourIndex + 1);
      closeTourStep(tourOrder[tourIndex]);
    }
  };

  const syncTourStep = (step: TourStep) => {
    if (tourOrder.includes(step) && step !== TourStep.Clear) {
      if (closedTourSteps.includes(step) && _.get(tourObj, [step, 'hasNext'], null)) {
        for (let i = tourOrder.indexOf(step); i < tourOrder.length; i++) {
          if (!closedTourSteps.includes(tourOrder[i])) {
            setTourIndex(i);
            break;
          }
        }
      } else if (step === TourStep.TaxonomyNext) {
        if (!closedTourSteps.includes(TourStep.Label)) {
          setTourIndex(tourOrder.indexOf(TourStep.Label));
        } else if (!closedTourSteps.includes(TourStep.PreviewTask)) {
          setTourIndex(tourOrder.indexOf(TourStep.PreviewTask));
        } else {
          setTourIndex(tourOrder.indexOf(step));
        }
      } else if (step === TourStep.InstructionsNext) {
        if (!closedTourSteps.includes(TourStep.Instructions)) {
          setTourIndex(tourOrder.indexOf(TourStep.Instructions));
        } else if (!closedTourSteps.includes(TourStep.PreviewInstructions)) {
          setTourIndex(tourOrder.indexOf(TourStep.PreviewInstructions));
        } else {
          setTourIndex(tourOrder.indexOf(step));
        }
      } else {
        setTourIndex(tourOrder.indexOf(step));
      }
    } else {
      setTourIndex(-1);
      setTourRef(null);
    }
  };

  const closeUpToCurrentView = (step: TourStep) => {
    // don't include current step
    if (tourOrder.includes(step)) {
      const toClose = [];
      for (const elem of tourOrder) {
        if (elem === step) {
          break;
        }
        if (!closedTourSteps.includes(elem)) {
          toClose.push(elem);
        }
      }
      setClosedTourSteps([...closedTourSteps, ...toClose]);
    }
  };

  const closeTourStep = (step: TourStep) => {
    if (!closedTourSteps.includes(step)) {
      if (step === TourStep.TaxonomyNext) {
        setClosedTourSteps([...closedTourSteps, step, TourStep.PreviewTask, TourStep.Label]);
      } else if (step === TourStep.InstructionsNext) {
        setClosedTourSteps([
          ...closedTourSteps,
          step,
          TourStep.PreviewInstructions,
          TourStep.Instructions,
        ]);
      } else {
        setClosedTourSteps([...closedTourSteps, step]);
      }
    }
  };

  const resetTour = () => {
    setClosedTourSteps([]);
    setTourIndex(0);
  };

  const handleExitTour = async () => {
    setClosedTourSteps(tourOrder);
    setShouldEnterTour(false);
    setTourIndex(0);
    try {
      (async () => {
        await API.selfserve.exitOnboardingTour({});
      })();
    } catch (_e) {
      setClosedTourSteps(tourOrder);
      setShouldEnterTour(false);
      setTourIndex(0);
    }
  };

  const handleEnterTour = async () => {
    try {
      (async () => {
        await API.selfserve.enterOnboardingTour({});
      })();
      resetTour();
      setShouldEnterTour(true);
    } catch (_e) {
      resetTour();
      setShouldEnterTour(true);
    }
  };

  useEffect(() => {
    const getShouldEnterTour = async () => {
      if (_.isNil(shouldEnterTour)) {
        const response = await API.selfserve.checkShouldEnterOnboardingTour({});
        setShouldEnterTour(response.shouldEnter);
        if (response.shouldEnter) {
          resetTour();
        } else {
          setClosedTourSteps(tourOrder);
        }
      }
    };

    getShouldEnterTour().catch(e => defaultApiFetchErrorHandler(e, { handleHttp: true }));
  }, [shouldEnterTour, tourOrder, firstTimeTemplate]);

  const tourObj = {
    [TourStep.CreateANewProject]: {
      name: TourStep.CreateANewProject,
      text: 'Hello! Start by finding a template that matches your use cases or create a custom project.',
      placement: 'right-end',
    },
    [TourStep.SampleDataset]: {
      name: TourStep.SampleDataset,
      text: 'Don’t have your data handy? Use a sample dataset instead! You can also add your own data later.',
      placement: 'right',
    },
    [TourStep.DataImported]: {
      name: TourStep.DataImported,
      text: 'Success! Your data has been imported. Click next to continue.',
      placement: 'bottom',
    },
    [TourStep.UseCase]: {
      name: TourStep.UseCase,
      text: 'Pick the use case that best fits your labeling needs. To see examples, click “Learn More.”',
      placement: 'right',
    },
    [TourStep.Pipeline]: {
      name: TourStep.Pipeline,
      text: 'A pipeline outlines the sequence in which labelers complete your task. Select one. ',
      placement: 'right',
    },
    [TourStep.UseCaseNext]: {
      name: TourStep.UseCaseNext,
      text: 'Click next to continue!',
      placement: 'bottom',
    },
    // the following 3 are all in the Taxonomy step
    [TourStep.Label]: {
      name: TourStep.Label,
      text: 'We’ve added some example labels to help you get started. Click into a label to edit its details',
      placement: 'left',
      hasNext: true,
    },
    [TourStep.PreviewTask]: {
      name: TourStep.PreviewTask,
      text: 'Check how your task will appear to labelers.',
      placement: 'right',
      hasNext: true,
    },
    [TourStep.TaxonomyNext]: {
      name: TourStep.TaxonomyNext,
      text: 'Click next when you have added all your labels!',
      placement: 'bottom',
    },
    // the following 3 are all in the Instructions step
    [TourStep.Instructions]: {
      name: TourStep.Instructions,
      text: 'Write your instructions. We’ve created sections to help you get started! Click into a section to edit its details.',
      placement: 'left',
      hasNext: true,
    },
    [TourStep.PreviewInstructions]: {
      name: TourStep.PreviewInstructions,
      text: 'Check how your instructions will look to labelers.',
      placement: 'right',
      hasNext: true,
    },
    [TourStep.InstructionsNext]: {
      name: TourStep.InstructionsNext,
      text: 'Click next when your instructions are ready!',
      placement: 'bottom',
    },
    [TourStep.BatchNext]: {
      name: TourStep.BatchNext,
      text: 'Click next to select your data for your first batch!',
      placement: 'left',
    },
    [TourStep.LaunchBatch]: {
      name: TourStep.LaunchBatch,
      text: 'Click here to launch your batch!',
      placement: 'left',
    },
    // template specific
    [TourStep.SelectTemplate]: {
      name: TourStep.SelectTemplate,
      text: 'Select this template if it matches your use case!',
      placement: 'bottom',
    },
    [TourStep.AddData]: {
      name: TourStep.AddData,
      text: 'Click here to add your own data! You can also upload more later. Or, click next to continue.',
      placement: 'bottom',
    },
    [TourStep.UseCaseTemplate]: {
      name: 'use_case_template',
      text: 'Your use case and pipeline are selected based on the template that you have chosen. Click next to continue!',
      placement: 'bottom',
    },
    [TourStep.TemplateLabel]: {
      name: 'template_label',
      text: 'This template comes with some labels to help you get started. Click into a label to edit its details',
      placement: 'left',
      hasNext: true,
    },
  };

  if (!showTour || !shouldEnterTour) {
    return {
      tourRef: null,
      setTourRef: () => {},
      currentTourStep: '',
      handleNextTourStep: () => {},
      syncTourStep: () => {},
      closeTourStep: () => {},
      closedTourSteps: [] as TourStep[],
      resetTour: () => {},
      handleEnterTour,
      handleExitTour: () => {},
      closeUpToCurrentView: () => {},
    };
  }
  return {
    tourRef,
    setTourRef,
    currentTourStep: _.get(tourObj, tourOrder[tourIndex], null),
    handleNextTourStep,
    syncTourStep,
    closeTourStep,
    closedTourSteps,
    resetTour,
    handleEnterTour,
    handleExitTour,
    closeUpToCurrentView,
  };
}
